import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Users, Shield, Layers, DollarSign, BarChart3, FileText, BookOpen, FileEdit, Settings, Flag, Video, Loader2, LifeBuoy } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { CAPS, hasCapability, hasAnyCapability } from '../utils/capabilities';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { user, authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;
    const isAdminOrSubAdmin = user?.role === 'admin' || user?.role === 'sub_admin';
    
    if (user && !isAdminOrSubAdmin) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    
    if (isAdminOrSubAdmin) {
      fetchStats();
    }
  }, [user, navigate, authLoading]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const isFullAdmin = user?.role === 'admin';
  const isSubAdmin = user?.role === 'sub_admin';

  // Hardcoded access rules:
  // - Sub-admins: Content Management (limited), User Management (limited)
  // - Admins only: Finances, Analytics

  const adminSections = [
    {
      title: 'Content Management',
      description: isSubAdmin 
        ? 'Review submissions, edit questions, and approve content'
        : 'Manage questions, functions, topics, and surveyors',
      icon: Layers,
      path: '/admin/content',
      color: 'text-[#6366F1]',
      bgColor: 'bg-[#6366F1]/10',
      stats: [
        { label: 'Questions', value: stats?.total_questions || 0 },
        { label: 'Pending', value: stats?.pending_questions || 0 },
        { label: 'Functions', value: stats?.total_functions || 0 }
      ],
      available: hasCapability(user, CAPS.MANAGE_CONTENT)
    },
    {
      title: 'User Management',
      description: isSubAdmin 
        ? 'View users, edit info, and reset passwords'
        : 'Manage users, roles, and permissions',
      icon: Users,
      // Sub-admins can only reach the capability-gated /admin/users page.
      // Full admins get the richer /admin/user-management surface.
      path: isFullAdmin ? '/admin/user-management' : '/admin/users',
      color: 'text-[#0A7EA4]',
      bgColor: 'bg-[#0A7EA4]/10',
      stats: [
        { label: 'Total Users', value: stats?.total_users || 0 }
      ],
      available: hasCapability(user, CAPS.MANAGE_USERS)
    },
    {
      title: 'Live Classes',
      description: isSubAdmin
        ? 'View your assigned classes and start as host'
        : 'Schedule classes, manage Zoom accounts, and assign hosts',
      icon: Video,
      path: '/admin/live-classes',
      color: 'text-[#FF6B6B]',
      bgColor: 'bg-[#FF6B6B]/10',
      stats: [
        { label: 'Upcoming Classes', value: stats?.upcoming_live_classes_count ?? 0 }
      ],
      available: hasCapability(user, CAPS.HOST_CLASSES)
    },
    {
      title: 'Finances',
      description: 'Payment settings, logs, and revenue tracking',
      icon: DollarSign,
      path: '/admin/finances',
      color: 'text-[#10B981]',
      bgColor: 'bg-[#10B981]/10',
      stats: [
        { label: 'Revenue', value: stats?.total_revenue ? `₹${stats.total_revenue.toFixed(0)}` : '₹0' },
        { label: 'Paid Users', value: stats?.paid_users || 0 },
        { label: 'Transactions', value: stats?.successful_payments || 0 }
      ],
      available: hasCapability(user, CAPS.VIEW_FINANCES),
      adminOnly: true
    },
    {
      title: 'Analytics',
      description: 'Platform usage statistics and user insights',
      icon: BarChart3,
      path: '/admin/analytics',
      color: 'text-[#8B5CF6]',
      bgColor: 'bg-[#8B5CF6]/10',
      stats: [
        { label: 'View Dashboard', value: '→' }
      ],
      available: hasCapability(user, CAPS.VIEW_ANALYTICS),
      adminOnly: true
    },
    {
      title: 'Page Builder',
      description: 'Manage legal pages, user guide, guidelines, and external links',
      icon: FileEdit,
      path: '/admin/page-builder',
      color: 'text-[#F59E0B]',
      bgColor: 'bg-[#F59E0B]/10',
      stats: [
        { label: 'Edit Content', value: '→' }
      ],
      // Page Builder is admin-only for now (Phase 4).
      available: user?.role === 'admin',
      adminOnly: true
    },
    {
      title: 'Admin Tools',
      description: 'Updates, module activation, and audit logs',
      icon: Settings,
      path: '/admin/tools',
      color: 'text-[#6366F1]',
      bgColor: 'bg-[#6366F1]/10',
      stats: [
        { label: 'Manage Settings', value: '→' }
      ],
      // Show the tools container if the sub-admin has ANY tool-relevant cap.
      // The tools grid itself filters per-cap.
      available: hasAnyCapability(user, [CAPS.MANAGE_UPDATES, CAPS.GRANT_COINS, CAPS.VIEW_SESSIONS]),
      adminOnly: true,
      badge: null
    },
    {
      // Admin Docs — internal knowledge base. Both full admins and
      // sub-admins land here; server-side capability filter decides
      // which articles a given sub-admin actually sees. The tile
      // itself is unconditionally shown to admin + sub_admin (there's
      // always at least one "visible to all" article to greet them).
      title: 'Admin Docs',
      description: 'How-to guides and product docs for the admin team',
      icon: LifeBuoy,
      path: '/admin/docs',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-500/10',
      stats: [
        { label: 'Browse guides', value: '→' }
      ],
      available: user?.role === 'admin' || user?.role === 'sub_admin',
      adminOnly: true,
      badge: null
    }
  ];

  // Show loading while auth is being verified
  if (authLoading) {
    return (
      <Layout>
        <div className="page-transition">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#0A7EA4]" />
            <span className="ml-3 text-gray-600">Loading...</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-transition">
        {/* Header */}
        <Card className="mb-8 border-l-4 border-l-[#FF6B6B] shadow-lg">
          <CardHeader className="bg-gradient-to-r from-[#FF6B6B]/5 to-[#0A7EA4]/5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-[#0A7EA4] rounded-lg">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <div>
                <CardTitle className="text-3xl text-[#0A7EA4]">Admin Dashboard</CardTitle>
                <CardDescription className="text-base mt-1">
                  {isFullAdmin ? 'Full administrative control' : 'Sub-admin access - Content & User Management'}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Admin Section Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          {adminSections.map((section, index) => (
            section.available && (
              <Card
                key={index}
                data-testid={`admin-section-${section.title.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => navigate(section.path)}
                className="cursor-pointer hover-lift border-2 border-gray-200 hover:border-[#0A7EA4] transition-all"
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className={`p-4 ${section.bgColor} rounded-lg mb-4 relative`}>
                      <section.icon className={`w-10 h-10 ${section.color}`} />
                      {section.badge && (
                        <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                          {section.badge > 99 ? '99+' : section.badge}
                        </span>
                      )}
                    </div>
                    {section.adminOnly && (
                      <Shield className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                  <CardTitle className="text-2xl">{section.title}</CardTitle>
                  <CardDescription className="text-base mt-2">
                    {section.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex gap-4 flex-wrap">
                    {section.stats.map((stat, statIndex) => (
                      <div key={statIndex} className="flex flex-col">
                        <div className={`text-2xl font-bold ${section.color}`}>
                          {stat.value}
                        </div>
                        <div className="text-xs text-gray-600">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default AdminDashboardPage;